"use client";

import styles from "./page.module.css";
import Footer from "@/components/Footer";

const PrivacyPolicyPage = () => {
  return (
    <div>
      <div className={styles.container}>
        <h1>
          <strong>Privacy Policy</strong>
        </h1>
        <p>Last updated December 9, 2025</p>
        <p>
          This Privacy Notice for Louvre LLC (doing business as instaticker)
          (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), describes how
          and why we might access, collect, store, use, and/or share
          (&quot;process&quot;) your personal information when you use our
          services (&quot;Services&quot;), including when you:
        </p>
        <ul>
          <li>
            Visit our website at{" "}
            <a href="https://instaticker.com/">https://instaticker.com/</a> or
            any website of ours that links to this Privacy Notice
          </li>
          <li>
            Download and use our mobile application (instaticker), or any other
            application of ours that links to this Privacy Notice
          </li>
          <li>
            Engage with us in other related ways, including any sales,
            marketing, or events
          </li>
        </ul>
        <p>
          Questions or concerns? Reading this Privacy Notice will help you
          understand your privacy rights and choices. We are responsible for
          making decisions about how your personal information is processed. If
          you do not agree with our policies and practices, please do not use
          our Services. If you still have any questions or concerns, please
          contact us at{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>.
        </p>
        <h2>
          <strong>SUMMARY OF KEY POINTS</strong>
        </h2>
        <p>
          <em>
            This summary provides key points from our Privacy Notice, but you
            can find out more details about any of these topics by clicking the
            link following each key point or by using our{" "}
            <a href="#table-of-contents">table of contents</a> below to find the
            section you are looking for.
          </em>
        </p>
        <p>
          What personal information do we process? When you visit, use, or
          navigate our Services, we may process personal information depending
          on how you interact with us and the Services, the choices you make,
          and the products and features you use. Learn more about{" "}
          <a href="#personal-information-you-disclose-to-us">
            personal information you disclose to us
          </a>
          .
        </p>
        <p>
          Do we process any sensitive personal information? Some of the
          information may be considered &quot;special&quot; or
          &quot;sensitive&quot; in certain jurisdictions, for example your
          racial or ethnic origins, sexual orientation, and religious beliefs.
          We do not process sensitive personal information.
        </p>
        <p>
          Do we collect any information from third parties? We do not collect
          any information from third parties.
        </p>
        <p>
          How do we process your information? We process your information to
          provide, improve, and administer our Services, communicate with you,
          for security and fraud prevention, and to comply with law. We may also
          process your information for other purposes with your consent. We
          process your information only when we have a valid legal reason to do
          so. Learn more about{" "}
          <a href="#2-how-do-we-process-your-information">
            how we process your information
          </a>
          .
        </p>
        <p>
          In what situations and with which parties do we share personal
          information? We may share information in specific situations and with
          specific third parties. Learn more about{" "}
          <a href="#3-when-and-with-whom-do-we-share-your-personal-information">
            when and with whom we share your personal information
          </a>
          .
        </p>
        <p>
          How do we keep your information safe? We have adequate organizational
          and technical processes and procedures in place to protect your
          personal information. However, no electronic transmission over the
          internet or information storage technology can be guaranteed to be
          100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Learn more about{" "}
          <a href="#7-how-do-we-keep-your-information-safe">
            how we keep your information safe
          </a>
          .
        </p>
        <p>
          What are your rights? Depending on where you are located
          geographically, the applicable privacy law may mean you have certain
          rights regarding your personal information. Learn more about{" "}
          <a href="#9-what-are-your-privacy-rights">your privacy rights</a>.
        </p>
        <p>
          How do you exercise your rights? The easiest way to exercise your
          rights is by visiting{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>,
          or by contacting us. We will consider and act upon any request in
          accordance with applicable data protection laws.
        </p>
        <p>
          Want to learn more about what we do with any information we collect?
          Review the Privacy Notice in full.
        </p>
        <h2 id="table-of-contents">
          <strong>TABLE OF CONTENTS</strong>
        </h2>
        <ol>
          <li>
            <a href="#1-what-information-do-we-collect">
              WHAT INFORMATION DO WE COLLECT?
            </a>
          </li>
          <li>
            <a href="#2-how-do-we-process-your-information">
              HOW DO WE PROCESS YOUR INFORMATION?
            </a>
          </li>
          <li>
            <a href="#3-when-and-with-whom-do-we-share-your-personal-information">
              WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </a>
          </li>
          <li>
            <a href="#4-do-we-use-cookies-and-other-tracking-technologies">
              DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </a>
          </li>
          <li>
            <a href="#5-do-we-offer-artificial-intelligence-based-products">
              DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?
            </a>
          </li>
          <li>
            <a href="#6-how-long-do-we-keep-your-information">
              HOW LONG DO WE KEEP YOUR INFORMATION?
            </a>
          </li>
          <li>
            <a href="#7-how-do-we-keep-your-information-safe">
              HOW DO WE KEEP YOUR INFORMATION SAFE?
            </a>
          </li>
          <li>
            <a href="#8-do-we-collect-information-from-minors">
              DO WE COLLECT INFORMATION FROM MINORS?
            </a>
          </li>
          <li>
            <a href="#9-what-are-your-privacy-rights">
              WHAT ARE YOUR PRIVACY RIGHTS?
            </a>
          </li>
          <li>
            <a href="#10-controls-for-do-not-track-features">
              CONTROLS FOR DO-NOT-TRACK FEATURES
            </a>
          </li>
          <li>
            <a href="#11-do-united-states-residents-have-specific-privacy-rights">
              DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
            </a>
          </li>
          <li>
            <a href="#12-do-we-make-updates-to-this-notice">
              DO WE MAKE UPDATES TO THIS NOTICE?
            </a>
          </li>
          <li>
            <a href="#13-how-can-you-contact-us-about-this-notice">
              HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </a>
          </li>
          <li>
            <a href="#14-how-can-you-review-update-or-delete-the-data-we-collect-from-you">
              HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </a>
          </li>
        </ol>
        <h2 id="1-what-information-do-we-collect">
          <strong>1. WHAT INFORMATION DO WE COLLECT?</strong>
        </h2>
        <h3 id="personal-information-you-disclose-to-us">
          <strong>Personal information you disclose to us</strong>
        </h3>
        <p>
          <em>
            In Short: We collect personal information that you provide to us.
          </em>
        </p>
        <p>
          We collect personal information that you voluntarily provide to us
          when you register on the Services, express an interest in obtaining
          information about us or our products and Services, when you
          participate in activities on the Services, or otherwise when you
          contact us.
        </p>
        <p>
          Personal Information Provided by You. The personal information that we
          collect depends on the context of your interactions with us and the
          Services, the choices you make, and the products and features you use.
          The personal information we collect may include the following:
        </p>
        <ul>
          <li>contact or authentication data</li>
        </ul>
        <p>Sensitive Information. We do not process sensitive information.</p>
        <p>
          Payment Data. We may collect data necessary to process your payment if
          you choose to make purchases, such as your payment instrument number,
          and the security code associated with your payment instrument. All
          payment data is handled and stored by Stripe. You may find their
          privacy notice link(s) here:{" "}
          <a href="https://stripe.com/privacy">https://stripe.com/privacy</a>.
        </p>
        <p>
          All personal information that you provide to us must be true,
          complete, and accurate, and you must notify us of any changes to such
          personal information.
        </p>
        <h3>
          <strong>Information automatically collected</strong>
        </h3>
        <p>
          <em>
            In Short: Some information — such as your Internet Protocol (IP)
            address and/or browser and device characteristics — is collected
            automatically when you visit our Services.
          </em>
        </p>
        <p>
          We automatically collect certain information when you visit, use, or
          navigate the Services. This information does not reveal your specific
          identity (like your name or contact information) but may include
          device and usage information, such as your IP address, browser and
          device characteristics, operating system, language preferences,
          referring URLs, device name, country, location, information about how
          and when you use our Services, and other technical information. This
          information is primarily needed to maintain the security and operation
          of our Services, and for our internal analytics and reporting
          purposes.
        </p>
        <p>
          Like many businesses, we also collect information through cookies and
          similar technologies.
        </p>
        <p>The information we collect includes:</p>
        <ul>
          <li>
            <em>Log and Usage Data.</em> Log and usage data is service-related,
            diagnostic, usage, and performance information our servers
            automatically collect when you access or use our Services and which
            we record in log files. Depending on how you interact with us, this
            log data may include your IP address, device information, browser
            type, and settings and information about your activity in the
            Services (such as the date/time stamps associated with your usage,
            pages and files viewed, searches, and other actions you take such as
            which features you use), device event information (such as system
            activity, error reports (sometimes called &quot;crash dumps&quot;),
            and hardware settings).
          </li>
          <li>
            <em>Device Data.</em> We collect device data such as information
            about your computer, phone, tablet, or other device you use to
            access the Services. Depending on the device used, this device data
            may include information such as your IP address (or proxy server),
            device and application identification numbers, location, browser
            type, hardware model, Internet service provider and/or mobile
            carrier, operating system, and system configuration information.
          </li>
          <li>
            <em>Location Data.</em> We collect location data such as information
            about your device&apos;s location, which can be either precise or
            imprecise. How much information we collect depends on the type and
            settings of the device you use to access the Services. For example,
            we may use GPS and other technologies to collect geolocation data
            that tells us your current location (based on your IP address). You
            can opt out of allowing us to collect this information either by
            refusing access to the information or by disabling your Location
            setting on your device. However, if you choose to opt out, you may
            not be able to use certain aspects of the Services.
          </li>
        </ul>
        <h2 id="2-how-do-we-process-your-information">
          <strong>2. HOW DO WE PROCESS YOUR INFORMATION?</strong>
        </h2>
        <p>
          <em>
            In Short: We process your information to provide, improve, and
            administer our Services, communicate with you, for security and
            fraud prevention, and to comply with law. We may also process your
            information for other purposes with your consent.
          </em>
        </p>
        <p>
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </p>
        <ul>
          <li>
            To facilitate account creation and authentication and otherwise
            manage user accounts. We may process your information so you can
            create and log in to your account, as well as keep your account in
            working order.
          </li>
          <li>
            To respond to user inquiries/offer support to users. We may process
            your information to respond to your inquiries and solve any
            potential issues you might have with the requested service.
          </li>
          <li>
            To fulfill and manage your orders. We may process your information
            to fulfill and manage your orders, payments, returns, and exchanges
            made through the Services.
          </li>
          <li>
            To enable user-to-user communications. We may process your
            information if you choose to use any of our offerings that allow for
            communication with another user.
          </li>
        </ul>
        <h2 id="3-when-and-with-whom-do-we-share-your-personal-information">
          <strong>
            3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </strong>
        </h2>
        <p>
          <em>
            In Short: We may share information in specific situations described
            in this section and/or with the following third parties.
          </em>
        </p>
        <p>
          Vendors, Consultants, and Other Third-Party Service Providers. We may
          share your data with third-party vendors, service providers,
          contractors, or agents (&quot;third parties&quot;) who perform
          services for us or on our behalf and require access to such
          information to do that work. We have contracts in place with our third
          parties, which are designed to help safeguard your personal
          information. This means that they cannot do anything with your
          personal information unless we have instructed them to do it. They
          will also not share your personal information with any organization
          apart from us. They also commit to protect the data they hold on our
          behalf and to retain it for the period we instruct.
        </p>
        <p>
          The third parties we may share personal information with are as
          follows:
        </p>
        <ul>
          <li>Content Optimization</li>
          <li>Google Site Search</li>
          <li>Invoice and Billing</li>
          <li>Stripe</li>
          <li>Web and Mobile Analytics</li>
          <li>Google Analytics</li>
          <li>Website Testing</li>
          <li>Cloudflare</li>
        </ul>
        <p>
          We also may need to share your personal information in the following
          situations:
        </p>
        <ul>
          <li>
            Business Transfers. We may share or transfer your information in
            connection with, or during negotiations of, any merger, sale of
            company assets, financing, or acquisition of all or a portion of our
            business to another company.
          </li>
        </ul>
        <h2 id="4-do-we-use-cookies-and-other-tracking-technologies">
          <strong>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</strong>
        </h2>
        <p>
          <em>
            In Short: We may use cookies and other tracking technologies to
            collect and store your information.
          </em>
        </p>
        <p>
          We may use cookies and similar tracking technologies (like web beacons
          and pixels) to gather information when you interact with our Services.
          Some online tracking technologies help us maintain the security of our
          Services and your account, prevent crashes, fix bugs, save your
          preferences, and assist with basic site functions.
        </p>
        <p>
          We also permit third parties and service providers to use online
          tracking technologies on our Services for analytics and advertising,
          including to help manage and display advertisements, to tailor
          advertisements to your interests, or to send abandoned shopping cart
          reminders (depending on your communication preferences). The third
          parties and service providers use their technology to provide
          advertising about products and services tailored to your interests
          which may appear either on our Services or on other websites.
        </p>
        <p>
          To the extent these online tracking technologies are deemed to be a
          &quot;sale&quot;/&quot;sharing&quot; (which includes targeted
          advertising, as defined under the applicable laws) under applicable US
          state laws, you can opt out of these online tracking technologies by
          submitting a request as described below under section &quot;
          <a href="#11-do-united-states-residents-have-specific-privacy-rights">
            DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </a>
          &quot;
        </p>
        <p>
          Specific information about how we use such technologies and how you
          can refuse certain cookies is set out in our Cookie Notice.
        </p>
        <h3>
          <strong>Google Analytics</strong>
        </h3>
        <p>
          We may share your information with Google Analytics to track and
          analyze the use of the Services. To opt out of being tracked by Google
          Analytics across the Services, visit{" "}
          <a href="https://tools.google.com/dlpage/gaoptout">
            https://tools.google.com/dlpage/gaoptout
          </a>
          . For more information on the privacy practices of Google, please
          visit the{" "}
          <a href="https://policies.google.com/privacy">
            Google Privacy &amp; Terms page
          </a>
          .
        </p>
        <h2 id="5-do-we-offer-artificial-intelligence-based-products">
          <strong>
            5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?
          </strong>
        </h2>
        <p>
          <em>
            In Short: We offer products, features, or tools powered by
            artificial intelligence, machine learning, or similar technologies.
          </em>
        </p>
        <p>
          As part of our Services, we offer products, features, or tools powered
          by artificial intelligence, machine learning, or similar technologies
          (collectively, &quot;AI Products&quot;). These tools are designed to
          enhance your experience and provide you with innovative solutions. The
          terms in this Privacy Notice govern your use of the AI Products within
          our Services.
        </p>
        <h3>
          <strong>Our AI Products</strong>
        </h3>
        <p>Our AI Products are designed for the following functions:</p>
        <ul>
          <li>Image analysis</li>
        </ul>
        <h3>
          <strong>How We Process Your Data Using AI</strong>
        </h3>
        <p>
          All personal information processed using our AI Products is handled in
          line with our Privacy Notice and our agreement with third parties.
          This ensures high security and safeguards your personal information
          throughout the process, giving you peace of mind about your
          data&apos;s safety.
        </p>
        <h2 id="6-how-long-do-we-keep-your-information">
          <strong>6. HOW LONG DO WE KEEP YOUR INFORMATION?</strong>
        </h2>
        <p>
          <em>
            In Short: We keep your information for as long as necessary to
            fulfill the purposes outlined in this Privacy Notice unless
            otherwise required by law.
          </em>
        </p>
        <p>
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this Privacy Notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements). No purpose in this notice
          will require us keeping your personal information for longer than the
          period of time in which users have an account with us.
        </p>
        <p>
          When we have no ongoing legitimate business need to process your
          personal information, we will either delete or anonymize such
          information, or, if this is not possible (for example, because your
          personal information has been stored in backup archives), then we will
          securely store your personal information and isolate it from any
          further processing until deletion is possible.
        </p>
        <h2 id="7-how-do-we-keep-your-information-safe">
          <strong>7. HOW DO WE KEEP YOUR INFORMATION SAFE?</strong>
        </h2>
        <p>
          <em>
            In Short: We aim to protect your personal information through a
            system of organizational and technical security measures.
          </em>
        </p>
        <p>
          We have implemented appropriate and reasonable technical and
          organizational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access the Services
          within a secure environment.
        </p>
        <h2 id="8-do-we-collect-information-from-minors">
          <strong>8. DO WE COLLECT INFORMATION FROM MINORS?</strong>
        </h2>
        <p>
          <em>
            In Short: We do not knowingly collect data from or market to
            children under 18 years of age.
          </em>
        </p>
        <p>
          We do not knowingly collect, solicit data from, or market to children
          under 18 years of age, nor do we knowingly sell such personal
          information. By using the Services, you represent that you are at
          least 18 or that you are the parent or guardian of such a minor and
          consent to such minor dependent’s use of the Services. If we learn
          that personal information from users less than 18 years of age has
          been collected, we will deactivate the account and take reasonable
          measures to promptly delete such data from our records. If you become
          aware of any data we may have collected from children under age 18,
          please contact us at{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>.
        </p>
        <h2 id="9-what-are-your-privacy-rights">
          <strong>9. WHAT ARE YOUR PRIVACY RIGHTS?</strong>
        </h2>
        <p>
          <em>
            In Short: You may review, change, or terminate your account at any
            time, depending on your country, province, or state of residence.
          </em>
        </p>
        <p>
          Withdrawing your consent: If we are relying on your consent to process
          your personal information, which may be express and/or implied consent
          depending on the applicable law, you have the right to withdraw your
          consent at any time. You can withdraw your consent at any time by
          contacting us by using the contact details provided in the section
          &quot;
          <a href="#13-how-can-you-contact-us-about-this-notice">
            HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </a>
          &quot; below.
        </p>
        <p>
          However, please note that this will not affect the lawfulness of the
          processing before its withdrawal nor, when applicable law allows, will
          it affect the processing of your personal information conducted in
          reliance on lawful processing grounds other than consent.
        </p>
        <h3>
          <strong>Account Information</strong>
        </h3>
        <p>
          If you would at any time like to review or change the information in
          your account or terminate your account, you can:
        </p>
        <ul>
          <li>Contact us using the contact information provided.</li>
        </ul>
        <p>
          Upon your request to terminate your account, we will deactivate or
          delete your account and information from our active databases.
          However, we may retain some information in our files to prevent fraud,
          troubleshoot problems, assist with any investigations, enforce our
          legal terms and/or comply with applicable legal requirements.
        </p>
        <p>
          Cookies and similar technologies: Most Web browsers are set to accept
          cookies by default. If you prefer, you can usually choose to set your
          browser to remove cookies and to reject cookies. If you choose to
          remove cookies or reject cookies, this could affect certain features
          or services of our Services.
        </p>
        <p>
          If you have questions or comments about your privacy rights, you may
          email us at{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>.
        </p>
        <h2 id="10-controls-for-do-not-track-features">
          <strong>10. CONTROLS FOR DO-NOT-TRACK FEATURES</strong>
        </h2>
        <p>
          Most web browsers and some mobile operating systems and mobile
          applications include a Do-Not-Track (&quot;DNT&quot;) feature or
          setting you can activate to signal your privacy preference not to have
          data about your online browsing activities monitored and collected. At
          this stage, no uniform technology standard for recognizing and
          implementing DNT signals has been finalized. As such, we do not
          currently respond to DNT browser signals or any other mechanism that
          automatically communicates your choice not to be tracked online. If a
          standard for online tracking is adopted that we must follow in the
          future, we will inform you about that practice in a revised version of
          this Privacy Notice.
        </p>
        <p>
          California law requires us to let you know how we respond to web
          browser DNT signals. Because there currently is not an industry or
          legal standard for recognizing or honoring DNT signals, we do not
          respond to them at this time.
        </p>
        <h2 id="11-do-united-states-residents-have-specific-privacy-rights">
          <strong>
            11. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </strong>
        </h2>
        <p>
          <em>
            In Short: If you are a resident of California, Colorado,
            Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland,
            Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
            Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the
            right to request access to and receive details about the personal
            information we maintain about you and how we have processed it,
            correct inaccuracies, get a copy of, or delete your personal
            information. You may also have the right to withdraw your consent to
            our processing of your personal information. These rights may be
            limited in some circumstances by applicable law. More information is
            provided below.
          </em>
        </p>
        <h3>
          <strong>Categories of Personal Information We Collect</strong>
        </h3>
        <p>
          The table below shows the categories of personal information we have
          collected in the past twelve (12) months. The table includes
          illustrative examples of each category and does not reflect the
          personal information we collect from you. For a comprehensive
          inventory of all personal information we process, please refer to the
          section &quot;
          <a href="#1-what-information-do-we-collect">
            WHAT INFORMATION DO WE COLLECT?
          </a>
          &quot;
        </p>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Examples</th>
              <th>Collected</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>A. Identifiers</td>
              <td>
                Contact details, such as real name, alias, postal address,
                telephone or mobile contact number, unique personal identifier,
                online identifier, Internet Protocol address, email address, and
                account name
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>
                B. Personal information as defined in the California Customer
                Records statute
              </td>
              <td>
                Name, contact information, education, employment, employment
                history, and financial information
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>
                C. Protected classification characteristics under state or
                federal law
              </td>
              <td>
                Gender, age, date of birth, race and ethnicity, national origin,
                marital status, and other demographic data
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>D. Commercial information</td>
              <td>
                Transaction information, purchase history, financial details,
                and payment information
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>E. Biometric information</td>
              <td>Fingerprints and voiceprints</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>F. Internet or other similar network activity</td>
              <td>
                Browsing history, search history, online behavior, interest
                data, and interactions with our and other websites,
                applications, systems, and advertisements
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>G. Geolocation data</td>
              <td>Device location</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>H. Audio, electronic, sensory, or similar information</td>
              <td>
                Images and audio, video or call recordings created in connection
                with our business activities
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>I. Professional or employment-related information</td>
              <td>
                Business contact details in order to provide you our Services at
                a business level or job title, work history, and professional
                qualifications if you apply for a job with us
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>J. Education Information</td>
              <td>Student records and directory information</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>K. Inferences drawn from collected personal information</td>
              <td>
                Inferences drawn from any of the collected personal information
                listed above to create a profile or summary about, for example,
                an individual’s preferences and characteristics
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>L. Sensitive personal Information</td>
              <td></td>
              <td>NO</td>
            </tr>
          </tbody>
        </table>
        <p>
          We may also collect other personal information outside of these
          categories through instances where you interact with us in person,
          online, or by phone or mail in the context of:
        </p>
        <ul>
          <li>Receiving help through our customer support channels;</li>
          <li>Participation in customer surveys or contests; and</li>
          <li>
            Facilitation in the delivery of our Services and to respond to your
            inquiries.
          </li>
        </ul>
        <h3>
          <strong>Sources of Personal Information</strong>
        </h3>
        <p>
          Learn more about the sources of personal information we collect in
          &quot;
          <a href="#1-what-information-do-we-collect">
            WHAT INFORMATION DO WE COLLECT?
          </a>
          &quot;
        </p>
        <h3>
          <strong>How We Use and Share Personal Information</strong>
        </h3>
        <p>
          Learn more about how we use your personal information in the section,
          &quot;
          <a href="#2-how-do-we-process-your-information">
            HOW DO WE PROCESS YOUR INFORMATION?
          </a>
          &quot;
        </p>
        <p>We collect and share your personal information through:</p>
        <ul>
          <li>Targeting cookies/Marketing cookies</li>
        </ul>
        <p>Will your information be shared with anyone else?</p>
        <p>
          We may disclose your personal information with our service providers
          pursuant to a written contract between us and each service provider.
          Learn more about how we disclose personal information to in the
          section, &quot;
          <a href="#3-when-and-with-whom-do-we-share-your-personal-information">
            WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </a>
          &quot;
        </p>
        <p>
          We may use your personal information for our own business purposes,
          such as for undertaking internal research for technological
          development and demonstration. This is not considered to be
          &quot;selling&quot; of your personal information.
        </p>
        <p>
          We have not sold or shared any personal information to third parties
          for a business or commercial purpose in the preceding twelve (12)
          months. We have disclosed the following categories of personal
          information to third parties for a business or commercial purpose in
          the preceding twelve (12) months:
          <br />
          The categories of third parties to whom we disclosed personal
          information for a business or commercial purpose can be found under
          &quot;
          <a href="#3-when-and-with-whom-do-we-share-your-personal-information">
            WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </a>
          &quot;
        </p>
        <h3>
          <strong>Your Rights</strong>
        </h3>
        <p>
          You have rights under certain US state data protection laws. However,
          these rights are not absolute, and in certain cases, we may decline
          your request as permitted by law. These rights include:
        </p>
        <ul>
          <li>
            Right to know whether or not we are processing your personal data
          </li>
          <li>Right to access your personal data</li>
          <li>Right to correct inaccuracies in your personal data</li>
          <li>Right to request the deletion of your personal data</li>
          <li>
            Right to obtain a copy of the personal data you previously shared
            with us
          </li>
          <li>Right to non-discrimination for exercising your rights</li>
          <li>
            Right to opt out of the processing of your personal data if it is
            used for targeted advertising (or sharing as defined under
            California’s privacy law), the sale of personal data, or profiling
            in furtherance of decisions that produce legal or similarly
            significant effects (&quot;profiling&quot;)
          </li>
        </ul>
        <p>
          Depending upon the state where you live, you may also have the
          following rights:
        </p>
        <ul>
          <li>
            Right to access the categories of personal data being processed (as
            permitted by applicable law, including the privacy law in Minnesota)
          </li>
          <li>
            Right to obtain a list of the categories of third parties to which
            we have disclosed personal data (as permitted by applicable law,
            including the privacy law in California, Delaware, and Maryland)
          </li>
          <li>
            Right to obtain a list of specific third parties to which we have
            disclosed personal data (as permitted by applicable law, including
            the privacy law in Minnesota and Oregon)
          </li>
          <li>
            Right to obtain a list of third parties to which we have sold
            personal data (as permitted by applicable law, including the privacy
            law in Connecticut)
          </li>
          <li>
            Right to review, understand, question, and depending on where you
            live, correct how personal data has been profiled (as permitted by
            applicable law, including the privacy law in Connecticut and
            Minnesota)
          </li>
          <li>
            Right to limit use and disclosure of sensitive personal data (as
            permitted by applicable law, including the privacy law in
            California)
          </li>
          <li>
            Right to opt out of the collection of sensitive data and personal
            data collected through the operation of a voice or facial
            recognition feature (as permitted by applicable law, including the
            privacy law in Florida)
          </li>
        </ul>
        <h3>
          <strong>How to Exercise Your Rights</strong>
        </h3>
        <p>
          To exercise these rights, you can contact us by emailing us at{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>,
          or by referring to the contact details at the bottom of this document.
        </p>
        <p>
          Under certain US state data protection laws, you can designate an
          authorized agent to make a request on your behalf. We may deny a
          request from an authorized agent that does not submit proof that they
          have been validly authorized to act on your behalf in accordance with
          applicable laws.
        </p>
        <h3>
          <strong>Request Verification</strong>
        </h3>
        <p>
          Upon receiving your request, we will need to verify your identity to
          determine you are the same person about whom we have the information
          in our system. We will only use personal information provided in your
          request to verify your identity or authority to make the request.
          However, if we cannot verify your identity from the information
          already maintained by us, we may request that you provide additional
          information for the purposes of verifying your identity and for -
          security or fraud-prevention purposes. + security or fraud-prevention
          purposes.{" "}
        </p>
        <p>
          If you submit the request through an authorized agent, we may need to
          collect additional information to verify your identity before
          processing your request and the agent will need to provide a written
          and signed permission from you to submit such request on your behalf.
        </p>
        <h3>
          <strong>Appeals</strong>
        </h3>
        <p>
          Under certain US state data protection laws, if we decline to take
          action regarding your request, you may appeal our decision by emailing
          us at{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>.
          We will inform you in writing of any action taken or not taken in
          response to the appeal, including a written explanation of the reasons
          for the decisions. If your appeal is denied, you may submit a
          complaint to your state attorney general.
        </p>
        <h3>
          <strong>California &quot;Shine The Light&quot; Law</strong>
        </h3>
        <p>
          California Civil Code Section 1798.83, also known as the &quot;Shine
          The Light&quot; law, permits our users who are California residents to
          request and obtain from us, once a year and free of charge,
          information about categories of personal information (if any) we
          disclosed to third parties for direct marketing purposes and the names
          and addresses of all third parties with which we shared personal
          information in the immediately preceding calendar year. If you are a
          California resident and would like to make such a request, please
          submit your request in writing to us by using the contact details
          provided in the section &quot;
          <a href="#13-how-can-you-contact-us-about-this-notice">
            HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </a>
          &quot;
        </p>
        <h2 id="12-do-we-make-updates-to-this-notice">
          <strong>12. DO WE MAKE UPDATES TO THIS NOTICE?</strong>
        </h2>
        <p>
          <em>
            In Short: Yes, we will update this notice as necessary to stay
            compliant with relevant laws.
          </em>
        </p>
        <p>
          We may update this Privacy Notice from time to time. The updated
          version will be indicated by an updated &quot;Revised&quot; date at
          the top of this Privacy Notice. If we make material changes to this
          Privacy Notice, we may notify you either by prominently posting a
          notice of such changes or by directly sending you a notification. We
          encourage you to review this Privacy Notice frequently to be informed
          of how we are protecting your information.
        </p>
        <h2 id="13-how-can-you-contact-us-about-this-notice">
          <strong>13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</strong>
        </h2>
        <p>
          If you have questions or comments about this notice, you may email us
          at{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>.
        </p>
        <h2 id="14-how-can-you-review-update-or-delete-the-data-we-collect-from-you">
          <strong>
            14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </strong>
        </h2>
        <p>
          Based on the applicable laws of your country or state of residence in
          the US, you may have the right to request access to the personal
          information we collect from you, details about how we have processed
          it, correct inaccuracies, or delete your personal information. You may
          also have the right to withdraw your consent to our processing of your
          personal information. These rights may be limited in some
          circumstances by applicable law. To request to review, update, or
          delete your personal information, please visit:{" "}
          <a href="mailto:support@instaticker.com">support@instaticker.com</a>.
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
